var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/errors/index.ts
var NoCoreWalletError = class _NoCoreWalletError extends Error {
  constructor() {
    super("Core wallet is not installed");
    this.name = _NoCoreWalletError.name;
    Object.setPrototypeOf(this, _NoCoreWalletError.prototype);
  }
};
var RpcError = class extends Error {
  constructor(code, message, internal, data) {
    if (!Number.isInteger(code))
      throw new Error('"code" must be an integer.');
    if (!message || typeof message !== "string")
      throw new Error('"message" must be a nonempty string.');
    super(message);
    this.code = code;
    this.data = data;
    this.internal = internal;
  }
};
var ProviderRpcError = class extends RpcError {
  /**
   * Create an Ethereum Provider JSON-RPC error.
   * `code` must be an integer in the 1000 <= 4999 range.
   */
  constructor(code, message, internal, data) {
    if (!(Number.isInteger(code) && code >= 1e3 && code <= 4999))
      throw new Error(
        '"code" must be an integer such that: 1000 <= code <= 4999'
      );
    super(code, message, internal, data);
  }
};
var ResourceUnavailableError = class extends RpcError {
  constructor(error) {
    super(-32002, "Resource unavailable", error);
    this.name = "ResourceUnavailable";
  }
};
var UserRejectedRequestError = class extends ProviderRpcError {
  constructor(error) {
    super(4001, "User rejected request", error);
    this.name = "UserRejectedRequestError";
  }
};

// src/connector/CoreWallet.ts
import { InjectedConnector } from "wagmi/connectors/injected";
import { getAddress } from "viem";
var CoreWalletConnector = class extends InjectedConnector {
  constructor({
    chains,
    options: options_
  } = {}) {
    const options = __spreadValues({
      name: "CoreWallet",
      shimDisconnect: true,
      shimChainChangedDisconnect: true
    }, options_);
    super({ chains, options });
    this.id = "CoreWallet";
    this.UNSTABLE_shimOnConnectSelectAccount = options.UNSTABLE_shimOnConnectSelectAccount;
  }
  connect() {
    return __async(this, arguments, function* ({ chainId } = {}) {
      var _a;
      try {
        const provider = yield this.getProvider();
        if (!provider)
          throw new NoCoreWalletError();
        if (provider.on && provider.isAvalanche) {
          provider.on("accountsChanged", this.onAccountsChanged);
          provider.on("chainChanged", this.onChainChanged);
          provider.on("disconnect", this.onDisconnect);
        }
        this.emit("message", { type: "connecting" });
        let account = null;
        if (this.UNSTABLE_shimOnConnectSelectAccount && ((_a = this.options) == null ? void 0 : _a.shimDisconnect)) {
          account = yield this.getAccount().catch(() => null);
          const isConnected = !!account;
          if (isConnected)
            yield provider.request({
              method: "wallet_requestPermissions",
              params: [{ eth_accounts: {} }]
            });
        }
        if (!account) {
          const accounts = yield provider.request({
            method: "eth_requestAccounts"
          });
          account = getAddress(accounts[0]);
        }
        let id = yield this.getChainId();
        let unsupported = this.isChainUnsupported(id);
        if (chainId && id !== chainId) {
          const chain = yield this.switchChain(chainId);
          id = chain.id;
          unsupported = this.isChainUnsupported(id);
        }
        return { account, chain: { id, unsupported }, provider };
      } catch (error) {
        if (this.isUserRejectedRequestError(error))
          throw new UserRejectedRequestError(error);
        if (error.code === -32002)
          throw new ResourceUnavailableError(error);
        throw error;
      }
    });
  }
  getProvider() {
    return __async(this, null, function* () {
      if (typeof window !== "undefined") {
        const provider = this.findProvider(
          window.avalanche
        );
        if (provider) {
          this.provider = provider;
        }
      }
      return this.provider;
    });
  }
  getReady(ethereum) {
    const isCoreWallet = !!(ethereum == null ? void 0 : ethereum.isAvalanche);
    if (!isCoreWallet)
      return;
    if (ethereum.isBraveWallet && !ethereum._events && !ethereum._state)
      return;
    if (ethereum.isKuCoinWallet)
      return;
    if (ethereum.isApexWallet)
      return;
    if (ethereum.isBitKeep)
      return;
    if (ethereum.isBlockWallet)
      return;
    if (ethereum.isMathWallet)
      return;
    if (ethereum.isOkxWallet || ethereum.isOKExWallet)
      return;
    if (ethereum.isOneInchIOSWallet || ethereum.isOneInchAndroidWallet)
      return;
    if (ethereum.isOpera)
      return;
    if (ethereum.isPortal)
      return;
    if (ethereum.isRabby)
      return;
    if (ethereum.isDefiant)
      return;
    if (ethereum.isTokenPocket)
      return;
    if (ethereum.isTokenary)
      return;
    if (ethereum.isZerion)
      return;
    return ethereum;
  }
  findProvider(ethereum) {
    if (ethereum == null ? void 0 : ethereum.providers)
      return ethereum.providers.find(this.getReady);
    return this.getReady(ethereum);
  }
};
export {
  CoreWalletConnector,
  NoCoreWalletError
};
